/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

import { logger } from "@/lib/logger";
import { supabase } from "@/lib/supabase";
import {
  AuthUser,
  RegisterData,
  login as authLogin,
  register as authRegister,
  logout as authLogout,
  refreshToken as authRefreshToken,
  getCurrentUser as authGetCurrentUser,
  fetchCurrentUser,
  hydrateUserFromSession,
  hasRole as authHasRole,
  initializeAuth,
  isAuthenticated,
} from "@/services/authService";
import {
  clearAllVersionedStorage,
  purgeOrphanedVersionKeys,
} from "@/utils/storage";

interface AuthContextValue {
  user: AuthUser | null;
  roles: string[];
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  getCurrentUser: () => AuthUser | null;
  hasRole: (role: string | string[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth on app start
  useEffect(() => {
    // Watchdog: if initAuth's await never returns (e.g. supabase.auth
    // getSession() rejects/hangs at the browser-lock layer — the
    // feedback_supabase_get_session_broken_in_prod pattern), `finally`
    // never runs and isLoading stays true forever. Force-recover by
    // treating as signed out so AuthGate redirects to /login.
    const WATCHDOG_MS = 8000;
    const watchdogStartedAt = Date.now();
    const watchdog = setTimeout(() => {
      logger.warn("AuthContext init watchdog tripped — forcing signed-out", {
        timeToFireMs: Date.now() - watchdogStartedAt,
        watchdogMs: WATCHDOG_MS,
        route: typeof window !== "undefined" ? window.location.pathname : null,
      });
      setUser(null);
      setIsLoading(false);
    }, WATCHDOG_MS);

    const initAuth = async () => {
      try {
        initializeAuth();
        const storedUser = authGetCurrentUser();

        if (storedUser) {
          // Try to fetch fresh user data from backend
          try {
            const freshUser = await fetchCurrentUser();
            setUser(freshUser || storedUser);
          } catch (fetchError) {
            logger.warn("Failed to fetch fresh user data, using stored user", {
              error: fetchError,
            });
            setUser(storedUser);
          }
        }
      } catch (error) {
        logger.error("Failed to initialize auth", error);
        setUser(null);
      } finally {
        clearTimeout(watchdog);
        setIsLoading(false);
      }
    };

    void initAuth();

    // Listen for auth state changes (handles OAuth redirects).
    //
    // IMPORTANT: this callback must NOT be async and must NOT call
    // getSession()/fetchCurrentUser() synchronously. auth-js fires it from
    // inside _initialize/_recoverAndRefresh while holding its auth lock; a
    // re-entrant getSession() then resolves with an error — the root cause of
    // the `getSession returned error during fetchCurrentUser` warning
    // (registry: supabase_get_session_root_cause_diagnosis, found 2026-06-01).
    // Use the `session` arg we already have, and defer the async hydration out
    // of the lock with setTimeout(0).
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session?.user) {
        const sessionUser = session.user;
        setTimeout(() => {
          void hydrateUserFromSession(sessionUser)
            .then((freshUser) => {
              if (freshUser) {
                setUser(freshUser);
              }
            })
            .catch((err) => {
              logger.error("Failed to hydrate user after OAuth sign-in", err);
            });
        }, 0);
      } else if (event === "SIGNED_OUT") {
        setUser(null);
      }
    });

    return () => {
      clearTimeout(watchdog);
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const loggedInUser = await authLogin(email, password);
      setUser(loggedInUser);
      logger.info("User logged in successfully");
    } catch (error) {
      logger.error("Login failed", error);
      throw error;
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      const registeredUser = await authRegister(userData);
      setUser(registeredUser);
      logger.info("User registered successfully");
    } catch (error) {
      logger.error("Registration failed", error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Clear all versioned storage FIRST (includes user preferences, filters, etc.)
      clearAllVersionedStorage();
      // …and keys left behind by earlier app versions, which the line above
      // cannot reach because it only matches the CURRENT version prefix. Those
      // orphans were exempt from every purge while still holding real data.
      purgeOrphanedVersionKeys();
      // Then sign out from backend
      await authLogout();
      setUser(null);
      logger.info("User logged out successfully");
    } catch (error) {
      logger.error("Logout failed", error);
      // Still clear user state even if backend call fails
      setUser(null);
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      const success = await authRefreshToken();
      if (success) {
        // Optionally fetch fresh user data after token refresh
        const freshUser = await fetchCurrentUser();
        if (freshUser) {
          setUser(freshUser);
        }
      } else {
        setUser(null);
      }
      return success;
    } catch (error) {
      logger.error("Token refresh failed", error);
      setUser(null);
      return false;
    }
  };

  const getCurrentUser = () => user;

  const hasRole = (role: string | string[]) => authHasRole(user, role);

  const userIsAuthenticated = user !== null && isAuthenticated();

  return (
    <AuthContext.Provider
      value={{
        user,
        roles: user?.roles ?? [],
        isLoading,
        isAuthenticated: userIsAuthenticated,
        login,
        register,
        logout,
        refreshToken,
        getCurrentUser,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
};

export { AuthContext };
